import { useState, useMemo, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import ChartConfigModal from "@plannel/components/modal/ChartConfigModal";
import Card, { CARD_HOVER_SX, CARD_HOVER_TRANSITION } from "@plannel/components/dashboard/DashboardCard";
import { KpiCard } from "@plannel/components/dashboard/KpiCard";
import { SegToggle } from "@plannel/components/dashboard/DashboardToggle";
import { DashboardPage, DashboardTopBar, DashboardRow } from "@plannel/components/dashboard/DashboardLayout";
import DashboardCardHeader from "@plannel/components/dashboard/DashboardCardHeader";
import CompactSelect from "@plannel/components/dashboard/CompactSelect";
import SeriesLegend from "@plannel/components/dashboard/SeriesLegend";
import { DashboardComposedChart, barOrLine, areaGradientDefs, DW_CHART_TYPES } from "@plannel/components/dashboard/DashboardChart";
import WorldMapPanel from "@plannel/components/dashboard/WorldMapPanel";
import {
  dashboardDefaultColDef, dashboardGridSx, DASHBOARD_ROW_HEIGHT, HIGHLIGHT_ROW_CLASS, RatioBarCell,
} from "@plannel/components/dashboard/dashboardGrid";
import { makeFmtMoney, fmtQty } from "@plannel/components/dashboard/dashboardFormat";
import useDashboardData from "@plannel/hooks/useDashboardData";
import useDashboardP13n from "@plannel/hooks/useDashboardP13n";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import ColorUtil from "@plannel/pages/utils/color-util";
import locationInventoryService from "@plannel/services/dashboard/location-inventory-service";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import ZDate from "@plannel/utils/zdate";
import { FS } from "@plannel/utils/font-util";

/*
 * Location Inventory Dashboard — RP(보충계획) 관점의 거점·네트워크 재고.
 *   산식·컬럼 명세: saas-application/docs/sql/dw/dashboard-spec/05_location_inventory.md
 *
 * 이 화면이 답하는 질문:
 *   "어느 거점이, 언제, 얼마나 목표(안전재고/수요)를 못 맞추며,
 *    그걸 메우려면 어느 lane(origin→dest)으로 얼마를 보내야 하는가."
 *
 * 다른 대시보드와의 경계 — Inventory Status = 실적 재고(과거) / Supply Plan = MP 생산·능력 /
 *   **이 화면 = RP 계획 재고(미래) + BOD 보충 네트워크**. 재고는 RP 계획 eoh 전용(실적을 섞지 않는다).
 */

/*
 * PSI 밸런스 시리즈. 색은 렌더 시점에 ColorUtil.colorSet(n) 으로 잡는다 —
 *   모듈 상수로 굳히면 앱 부팅 시 사용자 팔레트보다 먼저 default 로 고정돼 반영이 안 된다.
 *
 * 🚨 **이 카드는 수량 전용이다(금액 토글을 따르지 않는다).**
 *   RP fact 의 금액은 measure 마다 **단가 계열이 다르다** — 판매(demand_amt/sales_amt)는 std_unit_price,
 *   재고·발주(rp_order_amt/boh_amt/eoh_amt/target_inv_amt)는 inv_unit_cost 다(명세 §4-4).
 *   그래서 금액으로 그리면 `boh + 입고 − 출고 = eoh` 항등식이 **성립하지 않는다** — 밸런스 카드인데
 *   밸런스가 안 맞는 그림이 된다. 게다가 safety_inv_qty 는 금액 컬럼 자체가 없다.
 *   → 수량으로만 그린다. (재고일수 카드가 단위 토글과 무관한 것과 같은 이유)
 *
 * 축도 둘로 나눈다 — 잔량(1.3M 규모)과 흐름(50K 규모)이 한 축에 있으면 흐름 막대가 안 보인다.
 */
const PSI_KEYS = ["boh", "rpOrder", "demand", "sales", "eoh", "target", "safety"];
const PSI_STOCK_KEYS = ["boh", "eoh", "target", "safety"];   // 잔량 → 좌축 (나머지 = 흐름 → 우축)
const psiAxis = (k) => (PSI_STOCK_KEYS.includes(k) ? "stock" : "flow");

// 차트 설정 기본값(z_p13n config_obj.chart, 사용자별).
//   목표·안전재고는 **기준선** 성격이라 DASHED(스킬 §2). boh·sales 는 기본 숨김 — 축이 시끄러워진다.
const DEFAULT_CHART_CONFIG = {
  boh: { chartType: "LINE", activeFlg: false },
  rpOrder: { chartType: "BAR", activeFlg: true },
  demand: { chartType: "BAR", activeFlg: true },
  sales: { chartType: "BAR", activeFlg: false },
  eoh: { chartType: "AREA", activeFlg: true },
  target: { chartType: "DASHED", activeFlg: true },
  safety: { chartType: "DASHED", activeFlg: true },
};

// 위험도 = fact 의 risk_level(HIGH/MEDIUM/LOW). 팔레트가 아니라 **의미색**을 쓴다(위험은 지표가 아니라 상태).
const RiskChip = ({ value }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  if (!value) return null;
  const color = value === "HIGH" ? theme.palette.error.main
    : value === "MEDIUM" ? theme.palette.warning.main
      : theme.palette.success.main;
  const label = value === "HIGH" ? t("liRiskHigh") : value === "MEDIUM" ? t("liRiskMedium") : t("liRiskLow");
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", px: 0.7, borderRadius: 0.6,
      bgcolor: alpha(color, 0.15), color, fontSize: FS.BODY, fontWeight: 600, lineHeight: "18px",
    }}>
      {label}
    </Box>
  );
};

// ─────────────────────────────────────────
// 거점별 재고 현황 — 지도와 짝을 이루는 테이블(행 클릭 ↔ 마커 하이라이트 ↔ PSI 카드)
// ─────────────────────────────────────────
const LocationTable = ({ title, rows, currencyCd, unit, highlighted, onSelect, drill, hovered }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);
  const isAmt = unit === "AMT";
  const fmtBy = isAmt ? fmtMoney : fmtQty;
  const daysUnit = t("invDaysUnit");

  const columnDefs = useMemo(() => [
    { headerName: t("invColLoc"), field: "loc", flex: 1, minWidth: 108 },
    { headerName: t("liColLocType"), field: "locType", width: 62 },
    { headerName: t("eoh"), field: isAmt ? "eohAmt" : "eohQty", width: 86, type: "rightAligned",
      valueFormatter: (p) => fmtBy(p.value) },
    // 목표 대비 = Σeoh ÷ Σtarget × 100. 막대는 100% 에서 차지만 라벨은 실제 값(과잉이면 100 초과)
    { headerName: t("liColAttain"), field: "attainPct", width: 100, cellRenderer: RatioBarCell },
    // 커버리지 < LT = "리드타임 안에 못 채운다" → 강조. fact 의 low_coverage_flg 와 같은 판정식이다.
    { headerName: t("liColCoverage"), field: "coverageDays", width: 92, type: "rightAligned",
      valueFormatter: (p) => `${p.value} / ${p.data?.ltDays ?? "-"}${daysUnit}`,
      cellStyle: (p) => (p.value < (p.data?.ltDays ?? 0)
        ? { backgroundColor: alpha(theme.palette.warning.main, 0.15), color: theme.palette.warning.dark,
          fontWeight: 700, textAlign: "right" }
        : { textAlign: "right" }) },
    /* 결품 = 백오더(음수 재고)의 계획지평 최대치. short_qty(demand−sales)는 BACKORDER='allow' 계획에서
       구조적으로 0 이라 쓸 수 없다 — 엔진이 재고를 마이너스로 끌어다 전량 출하하기 때문(명세 §1-⑤). */
    { headerName: t("backorder"), field: isAmt ? "backlogAmt" : "backlogQty", width: 88, type: "rightAligned",
      valueFormatter: (p) => fmtBy(p.value) },
    { headerName: t("fillRate"), field: "fillRate", width: 70, type: "rightAligned",
      valueFormatter: (p) => `${p.value}%` },
    { headerName: t("liColRpOrder"), field: isAmt ? "rpOrderAmt" : "rpOrderQty", width: 86, type: "rightAligned",
      valueFormatter: (p) => fmtBy(p.value) },
    { headerName: t("liColRisk"), field: "risk", width: 70, cellRenderer: RiskChip },
  ], [t, theme, fmtBy, isAmt, daysUnit]);

  return (
    // 그리드 행 클릭(거점 선택)이 있어 **제목만** 드릴 링크로 잡는다(§9-1)
    <Card sx={{ flex: 5, minHeight: 0, transition: CARD_HOVER_TRANSITION, ...(hovered && CARD_HOVER_SX) }}>
      <DashboardCardHeader title={title} dense {...drill} />
      <Box className="ag-theme-balham" sx={dashboardGridSx(theme)}>
        <AgGridReact
          theme="legacy"
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={dashboardDefaultColDef}
          rowHeight={DASHBOARD_ROW_HEIGHT}
          headerHeight={32}
          suppressCellFocus
          onRowClicked={(e) => onSelect?.(e.data?.loc)}
          getRowClass={(p) => (p.data?.loc === highlighted ? HIGHLIGHT_ROW_CLASS : "")}
        />
      </Box>
    </Card>
  );
};

// ─────────────────────────────────────────
// 거점 PSI 밸런스 추이 — 이 화면의 유일한 시계열 카드.
//   boh + 보충발주 − 수요 = eoh 를 시점으로 보여주고 목표·안전재고를 기준선으로 깐다.
//   → **"언제 안전재고를 깨는가"** 가 이 카드의 존재 이유다(RP 의 본질).
// ─────────────────────────────────────────
const PsiBalanceCard = ({
  title, series, colors, labels, chartConfig, onToggleSeries, onOpenConfig,
  buckets, bucket, onChangeBucket, drill, hovered,
}) => {
  const { t } = useTranslation();
  const isActive = (k) => chartConfig?.[k]?.activeFlg !== false;
  const legend = PSI_KEYS.map((k) => ({ key: k, label: labels[k], color: colors[k] }));
  const data = series ?? [];

  return (
    // 차트 안에 범례·기어·버킷 토글이 있어 **제목만** 드릴 링크로 잡는다(§9-1)
    <Card sx={{ flex: 1, minHeight: 0, transition: CARD_HOVER_TRANSITION, ...(hovered && CARD_HOVER_SX) }}>
      <DashboardCardHeader title={title} onOpenConfig={onOpenConfig} {...drill}>
        {/* 이 카드는 수량 전용이라 명시한다 — 페이지 단위 토글(AMT/QTY)을 따르지 않는다(위 주석) */}
        <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{t("qty")}</Typography>
        {/* 버킷은 **카드별 독립 state**(스킬 §6). 계획의 native 버킷 이상으로 거친 단위만 온다(명세 §0-2) */}
        {buckets.length > 1 && <SegToggle value={bucket} options={buckets} onChange={onChangeBucket} />}
      </DashboardCardHeader>
      <DashboardComposedChart
        data={data}
        /* 잔량(≈1.3M)과 흐름(≈50K)은 자릿수가 달라 한 축에 두면 흐름 막대가 축에 붙어 안 보인다 */
        yAxes={[
          { id: "stock", tickFormatter: fmtQty },
          { id: "flow", orientation: "right", tickFormatter: fmtQty },
        ]}
        tooltipFormatter={(v) => fmtQty(v)}
      >
        {/* AREA 로 바꿀 수 있는 시리즈는 **전부** 등록해야 한다 — 빠지면 면이 안 칠해진다(스킬 §2) */}
        {areaGradientDefs(PSI_KEYS.map((k) => ({ dataKey: k, color: colors[k] })))}
        {PSI_KEYS.filter(isActive).map((k) => barOrLine(chartConfig, {
          key: k, dataKey: k, name: labels[k], color: colors[k], dflt: DEFAULT_CHART_CONFIG[k].chartType,
          yAxisId: psiAxis(k),
        }))}
      </DashboardComposedChart>
      <SeriesLegend items={legend} isActive={isActive} onToggle={onToggleSeries} />
    </Card>
  );
};

// ─────────────────────────────────────────
// 보충 예외 — 부족/조달리스크 · 과잉 두 탭
// ─────────────────────────────────────────
/*
 * 품목 축 컬럼 — 이 화면에서 **품목 계층 레벨이 실제로 도는 유일한 자리**다.
 *   최하위 레벨 = 품목 상세(코드·명·ABC), 상위 레벨 = 계층 노드 한 컬럼(서버가 접어서 준다).
 *   헤더는 선택한 레벨 라벨(z_hrchy_config.name — 테넌트 config 값이라 번역 대상이 아니다).
 * ★ ABC 는 그룹에서 의미가 없어 뺀다(등급은 품목 속성이지 그룹 속성이 아니다).
 */
const itemAxisCols = (t, grouped, groupLabel) => (grouped
  ? [{ headerName: groupLabel, field: "itemGrp", flex: 1, minWidth: 160 }]
  : [
    { headerName: t("invColItemCd"), field: "itemCd", flex: 1, minWidth: 132 },
    { headerName: t("invColItemNm"), field: "itemNm", flex: 1, minWidth: 110 },
    { headerName: t("invColAbc"), field: "invClass", width: 68, cellRenderer: AbcXyzChip },
  ]);

const ExceptionCard = ({ title, rows, currencyCd, unit, drill, hovered, grouped, groupLabel }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [tab, setTab] = useState("shortage");
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);
  const isAmt = unit === "AMT";
  const daysUnit = t("invDaysUnit");

  // 거점명은 짧고(서울점·US West DC) 품목코드는 길다(RD_ENT_01) → 폭 배분을 코드 쪽으로 옮긴다
  const shortageCols = useMemo(() => [
    { headerName: t("invColLoc"), field: "loc", width: 108 },
    ...itemAxisCols(t, grouped, groupLabel),
    /* 그룹 롤업일 때 이 값은 평균이 아니라 **그룹 안 최악값**이다(커버리지 최소 / LT 최대).
       예외 카드는 "가장 위험한 것부터"가 목적이라 평균으로 접으면 위험 품목이 묻힌다. */
    { headerName: t("liColCoverage"), field: "coverageDays", width: 90, type: "rightAligned",
      valueFormatter: (p) => `${p.value} / ${p.data?.ltDays ?? "-"}${daysUnit}` },
    { headerName: t("backorder"), field: isAmt ? "backlogAmt" : "backlogQty", width: 88, type: "rightAligned",
      valueFormatter: (p) => (isAmt ? fmtMoney(p.value) : fmtQty(p.value)),
      cellStyle: { backgroundColor: alpha(theme.palette.error.main, 0.15), color: theme.palette.error.dark,
        fontWeight: 700, textAlign: "right" } },
    { headerName: t("liColRpOrder"), field: "rpOrderQty", width: 88, type: "rightAligned",
      valueFormatter: (p) => fmtQty(p.value) },
  ], [t, theme, fmtMoney, isAmt, daysUnit, grouped, groupLabel]);

  const excessCols = useMemo(() => [
    { headerName: t("invColLoc"), field: "loc", width: 108 },
    ...itemAxisCols(t, grouped, groupLabel),
    { headerName: t("eoh"), field: "eohAmt", width: 84, type: "rightAligned",
      valueFormatter: (p) => fmtMoney(p.value) },
    { headerName: t("targetInventory"), field: "targetAmt", width: 84, type: "rightAligned",
      valueFormatter: (p) => fmtMoney(p.value) },
    { headerName: t("liColExcess"), field: "excessAmt", width: 88, type: "rightAligned",
      valueFormatter: (p) => fmtMoney(p.value),
      cellStyle: { backgroundColor: alpha(theme.palette.warning.main, 0.15), color: theme.palette.warning.dark,
        fontWeight: 700, textAlign: "right" } },
  ], [t, theme, fmtMoney, grouped, groupLabel]);

  const isShortage = tab === "shortage";
  return (
    // 제목 hover 상태를 카드에 펼쳐 KPI 카드와 같은 하이라이트를 낸다(자식 hover 로 부모를 못 칠한다)
    <Card sx={{ flex: 1, minHeight: 0, transition: CARD_HOVER_TRANSITION, ...(hovered && CARD_HOVER_SX) }}>
      {/* 카드 안에 그리드·탭 토글이 있어 **제목만** 드릴 링크로 잡는다(스킬 §9-1) — 전파 차단이 필요 없다 */}
      <DashboardCardHeader title={title} dense {...drill}>
        <SegToggle value={tab} options={["shortage", "excess"]} onChange={setTab} />
      </DashboardCardHeader>
      <Box className="ag-theme-balham" sx={dashboardGridSx(theme)}>
        <AgGridReact
          theme="legacy"
          rowData={isShortage ? (rows?.shortage ?? []) : (rows?.excess ?? [])}
          columnDefs={isShortage ? shortageCols : excessCols}
          defaultColDef={dashboardDefaultColDef}
          rowHeight={DASHBOARD_ROW_HEIGHT}
          headerHeight={32}
          suppressCellFocus
        />
      </Box>
    </Card>
  );
};

// 좌표가 하나도 없을 때 — 빈 지도를 띄우지 않고 이유를 말한다(명세 §2-지도).
//   z_location.latitude/longitude 는 v26.10 에서 추가된 컬럼이라 미입력 테넌트가 있다.
const NoGeoPanel = () => {
  const { t } = useTranslation();
  return (
    <Card sx={{ flex: 4, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", textAlign: "center", px: 2 }}>
        {t("liNoGeo")}
      </Typography>
    </Card>
  );
};

// ─────────────────────────────────────────
// 메인
// ─────────────────────────────────────────
const LocationInventoryDashboard = ({ viewName }) => {
  const { t } = useTranslation();
  const { isPermitted } = useMenuAccess();
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [unit, setUnit] = useState(reduxViewState?.unit ?? "AMT");   // 페이지 공통 단위(스킬 §1 — 페이지에 하나)
  const [itemLevel, setItemLevel] = useState("");                    // 재조회 대상
  const [locType, setLocType] = useState("");                        // 재조회 대상. 기본은 항상 전체("")
  /*
   * 지도 ↔ 테이블 ↔ PSI 카드 연동.
   * ★ **드릴로 주입된 거점은 받는다**(PSI Balance 의 불균형 행 → 그 거점의 PSI 시계열).
   *   저장은 여전히 안 한다(아래 useEffect 는 unit 만 쓴다) — "내가 나갔다 오면 전체" 는 그대로고,
   *   "다른 화면이 짚어 준 거점" 만 초기값으로 받는 것이다. 둘은 다른 얘기다.
   *   값은 **거점명**이다 — 이 화면이 `r.loc === selectedLoc` 으로 매칭하기 때문(코드가 아니다).
   */
  const [selectedLoc, setSelectedLoc] = useState(reduxViewState?.selectedLoc ?? null);
  const [psiBucket, setPsiBucket] = useState(null);                  // null = 서버 기본값(week 우선)
  const [configOpen, setConfigOpen] = useState(false);
  const [drillHoverKey, setDrillHoverKey] = useState(null);          // 제목 hover 중인 카드 key → 하이라이트

  // 재조회는 집계 레벨·거점 유형뿐 — 단위 토글과 거점 선택은 응답 안에서 해결한다(스킬 §6)
  const { payload } = useDashboardData(locationInventoryService.get, {
    itemLevel: itemLevel || undefined,
    locType: locType || undefined,
  });

  const { chartConfig, saveChartConfig, toggleSeries } =
    useDashboardP13n(viewName, { defaultChartConfig: DEFAULT_CHART_CONFIG });

  useEffect(() => {
    // 단위만 저장 — 거점 유형·선택 거점은 저장하지 않는다(새로 열면 항상 전체)
    const next = reduxUtil.createObjectState(viewName, { unit });
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  const fmtMoney = useMemo(() => makeFmtMoney(payload?.currencyCd), [payload]);
  const psiColors = useMemo(
    () => PSI_KEYS.reduce((acc, k, i) => ({ ...acc, [k]: ColorUtil.colorSet(i) }), {}),
    [],
  );
  const psiLabels = useMemo(() => ({
    boh: t("boh"), rpOrder: t("liSeriesRpOrder"), demand: t("demandQty"),
    sales: t("salesQty"), eoh: t("eoh"), target: t("targetInventory"), safety: t("safetyStock"),
  }), [t]);
  // 설정 모달 measure — 색은 차트와 같은 소스라 스와치가 어긋나지 않는다
  const configMeasures = useMemo(
    () => PSI_KEYS.map((k) => ({ key: k, label: psiLabels[k], hasActive: true, color: psiColors[k] })),
    [psiLabels, psiColors],
  );

  if (!payload) return null;

  const cc = payload.currencyCd;
  const kpis = payload.kpis ?? {};
  const isAmt = unit === "AMT";
  const fmtBy = isAmt ? fmtMoney : fmtQty;
  const daysUnit = t("invDaysUnit");

  // 지도 마커 = dest 거점(재고 보유 주체). 좌표 없는 거점은 지도에서만 빠지고 테이블엔 남는다.
  const mapItems = (payload.network?.nodes ?? []).filter((n) => n.lat != null && n.lng != null);

  // 테이블 정렬 = 계획재고 내림차순. **지도 마커 색이 이 순위를 따르므로 둘이 어긋나면 안 된다.**
  const locationRows = [...(payload.locationRows ?? [])].sort((a, b) => (b.eohAmt ?? 0) - (a.eohAmt ?? 0));

  /*
   * PSI 시리즈 — `psiBalance[버킷][거점]` 2단 맵. 거점 선택도 버킷 전환도 재조회가 없다(명세 §3-⑥).
   * 버킷 후보는 서버가 계획의 native 단위 이상으로 거친 것만 준다(명세 §0-2) — 화면은 쪼개지 않는다.
   */
  const psiBuckets = payload.availableBuckets ?? ["month"];
  // 기본 = 주(있으면), 없으면 가장 거친 단위. 일 계획 90일이면 13주로 떨어져 읽기 좋다.
  const activeBucket = psiBuckets.includes(psiBucket)
    ? psiBucket
    : (psiBuckets.includes("week") ? "week" : psiBuckets[psiBuckets.length - 1]);
  const selectedRow = locationRows.find((r) => r.loc === selectedLoc);
  const psiByLoc = payload.psiBalance?.[activeBucket] ?? {};
  const psiData = psiByLoc[selectedRow?.locId] ?? psiByLoc.all ?? [];
  const psiTitle = selectedLoc ? `${t("liPsiCard")} · ${selectedLoc}` : t("liPsiCard");

  /*
   * 목표재고 달성률이 100 미만이면 **재고가 늘어야 좋고**, 초과면 줄어야 좋다.
   * 부호만으로 칠하는 기본 동작(올라가면 좋다)은 과잉 상태에서 재고가 줄어드는 걸 나쁘게 보여준다
   * → deltaGood 으로 뒤집는다(스킬 §주의의 델타 함정 2번).
   */
  const attain = kpis.targetAttain?.value ?? 0;
  const eohDelta = kpis.projectedEoh?.deltaPct;
  const eohDeltaGood = eohDelta == null ? undefined : (attain < 100 ? eohDelta >= 0 : eohDelta < 0);

  const itemLevels = payload.itemLevels ?? [];
  const locTypeOptions = [{ value: "", label: t("liAllLocType") }, ...(payload.locTypes ?? [])];

  /*
   * 품목 계층 레벨은 **예외 카드에만** 적용된다 — 나머지 카드는 이미 전 품목 합이라 레벨과 무관하다.
   *   접힘 여부는 서버 판단(itemGrouped)을 그대로 따른다. 그룹 컬럼 헤더 = 선택 레벨 라벨.
   */
  const activeLevel = itemLevels.find((lv) => lv.value === (payload.itemLevel ?? ""));
  const itemGroupLabel = activeLevel?.label || activeLevel?.cd || t("itemLevel");

  /*
   * ── 드릴스루 (스킬 §9) ────────────────────────────────────────────────
   * 대상은 전부 RP 모듈 화면이고, 배포망(마스터 화면)만 빼면 같은 것을 읽는다:
   *   · rpVersion : 최신 RELEASED RP 버전. `{ id }` 만 심으면 RpVersionFilter 가 전체 객체를 찾는다
   *   · 거점 필터 : 화면마다 **키가 다르다** — `filter.locations`(INV_EXC·FILL_RATE) vs
   *                `filter.destLocation`(RP_REVIEW·RP_ORDER_LIST·PSI_SIMULATION). 값 모양은 `[{ locCd }]` 로 같다
   *   · category  : null = 전체 품목
   * ★ 안 심은 상태는 대상 페이지 기본값이 된다 — 그게 보통 원하는 결과다(스킬 §9 ③).
   */
  const drillTo = (target, state) => {
    reduxDispatch(reduxUtil.removeViewState({ viewName: target }));   // 이전 필터 제거(주입값 확실 적용)
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(target, state)));
    reduxDispatch(reduxUtil.updateHistoryState(target));              // 탭 전환
  };
  const rpVersion = payload.latestRpVersionId != null ? { id: payload.latestRpVersionId } : null;
  /* 선택한 거점을 그대로 넘긴다. **미선택이면 null = 전체 거점**(빈 배열을 넘기면 "아무 거점도 없음"이 된다).
     locCd 는 운영 코드다 — dest_loc_id(DW dim id)는 RP 화면 필터에 안 통한다. */
  const drillLoc = () => (selectedRow?.locCd ? [{ locCd: selectedRow.locCd, locName: selectedRow.loc }] : null);
  const destFilter = () => ({ originLocation: null, destLocation: drillLoc(), category: null });
  const locFilter = () => ({ locations: drillLoc(), category: null });
  // 계획 구간(yyyy-MM) → ZDate 월 경계. 시작=1일 0시, 끝=말일 23:59:59
  const drillPeriod = () => {
    const [fy, fm] = (payload.period?.from ?? "").split("-").map(Number);
    const [ty, tm] = (payload.period?.to ?? "").split("-").map(Number);
    return fy && ty ? { start: new ZDate(fy, fm - 1, 1, 0, 0, 0), end: new ZDate(ty, tm, 0, 23, 59, 59) } : null;
  };

  const openRpReview = () => drillTo("RP_REVIEW", { rpVersion, filter: destFilter() });
  const openRpOrderList = () => drillTo("RP_ORDER_LIST", {
    rpVersion, filter: destFilter(), period: drillPeriod(),
  });
  const openInventoryExceptions = () => drillTo("INV_EXC", { rpVersion, filter: locFilter() });
  const openFillRate = () => drillTo("FILL_RATE_DASHBOARD", { rpVersion, filter: locFilter() });
  const openPsiSimulation = () => drillTo("PSI_SIMULATION", { rpVersion, filter: destFilter() });
  /* 배포망은 계층 마스터 화면이라 버전·기간이 없다. 선택 거점만 넘긴다 —
     `selectedLocation` 은 노드 객체이고 `locCd` 로 그래프를 하이라이트한다(RpDistributionNetwork.js:296).
     ⚠ `nodeType` 은 넘기지 않는다(그 화면의 root/leaf 판정값이라 loc_type 과 다르다 → 넘기면 버튼 활성 판정이 틀어진다.
        없으면 setDisabled 가 true(비활성)로 떨어져 안전하다). */
  const openDistNetwork = () => drillTo("RP_DIST_NETWORK", {
    type: "GRAPH",
    selectedLocation: selectedRow?.locCd ? { locCd: selectedRow.locCd, name: selectedRow.loc } : null,
  });

  // 카드 key → 드릴 핸들러 + 대상 리포트 이름. hover 는 key 하나만 state 에 둔다(§9-1)
  // 권한 밖 화면으로 가는 드릴은 링크를 만들지 않는다 — onDrill 이 없으면 카드 제목·KPI 가 평문이 된다.
  const drillIf = (target, fn) => (isPermitted(target) ? fn : undefined);

  const drillDefs = {
    eoh: { onDrill: drillIf("RP_REVIEW", openRpReview), drillTitle: t("menuRpReview") },
    attain: { onDrill: drillIf("RP_REVIEW", openRpReview), drillTitle: t("menuRpReview") },
    replen: { onDrill: drillIf("RP_ORDER_LIST", openRpOrderList), drillTitle: t("menuRpOrderList") },
    coverage: { onDrill: drillIf("INV_EXC", openInventoryExceptions), drillTitle: t("menuRpInventoryExceptions") },
    shortage: { onDrill: drillIf("FILL_RATE_DASHBOARD", openFillRate), drillTitle: t("menuRpFillRateDashboard") },
    map: { onDrill: drillIf("RP_DIST_NETWORK", openDistNetwork), drillTitle: t("menuRpDistributionNetwork") },
    locTable: { onDrill: drillIf("RP_REVIEW", openRpReview), drillTitle: t("menuRpReview") },
    psi: { onDrill: drillIf("PSI_SIMULATION", openPsiSimulation), drillTitle: t("menuRpPsiSimulation") },
    exception: { onDrill: drillIf("INV_EXC", openInventoryExceptions), drillTitle: t("menuRpInventoryExceptions") },
  };
  // 제목 링크 카드용 — hover 를 받아 카드에 CARD_HOVER_SX 를 입힌다(KPI 카드와 같은 하이라이트)
  const titleDrill = (key) => ({
    ...drillDefs[key],
    onDrillHover: (on) => setDrillHoverKey(on ? key : null),
  });

  return (
    <DashboardPage>
      <DashboardTopBar period={payload.period} pinViewName={viewName}>
        {itemLevels.length > 1 && (
          <CompactSelect
            value={itemLevel || payload.itemLevel || ""}
            options={itemLevels.map((lv) => ({
              value: lv.value,
              label: lv.label || lv.cd || `${t("level")} ${lv.value}`,
            }))}
            onChange={setItemLevel}
          />
        )}
        <CompactSelect displayEmpty value={locType} options={locTypeOptions} onChange={setLocType} />
        <SegToggle value={unit} options={["AMT", "QTY"]} onChange={setUnit} />
      </DashboardTopBar>

      {/* Row 1: KPI 5장 — 기준월 스냅샷 기준(명세 §1).
          KPI 는 안에 클릭 요소가 없어 **카드 전체**가 드릴 링크가 된다(§9-1) */}
      <DashboardRow kpi columns={5}>
        <KpiCard
          label={t("liKpiProjectedEoh")}
          value={fmtBy(isAmt ? kpis.projectedEoh?.amt : kpis.projectedEoh?.qty)}
          deltaPct={eohDelta}
          deltaGood={eohDeltaGood}
          sub={payload.baseMonth}
          sparkData={payload.spark?.projectedEoh}
          gradientId="sparkLiEoh"
          onClick={drillDefs.eoh.onDrill}
        />
        <KpiCard
          label={t("liKpiTargetAttain")}
          value={`${attain}%`}
          sub={fmtBy(isAmt ? kpis.targetAttain?.targetAmt : kpis.targetAttain?.targetQty)}
          sparkData={payload.spark?.targetAttain}
          gradientId="sparkLiAttain"
          onClick={drillDefs.attain.onDrill}
        />
        <KpiCard
          label={t("liKpiPlannedReplen")}
          value={fmtBy(isAmt ? kpis.plannedReplen?.amt : kpis.plannedReplen?.qty)}
          sub={`${t("liLanes")} ${kpis.plannedReplen?.laneCount ?? 0}`}
          onClick={drillDefs.replen.onDrill}
        />
        <KpiCard
          label={t("liKpiCoverage")}
          value={`${kpis.coverage?.coverageDays ?? 0}${daysUnit}`}
          sub={`${t("liLeadTime")} ${kpis.coverage?.ltDays ?? 0}${daysUnit} · ${t("liLowCoverage")} ${kpis.coverage?.lowCoverageCount ?? 0}`}
          sparkData={payload.spark?.coverage}
          gradientId="sparkLiCoverage"
          onClick={drillDefs.coverage.onDrill}
        />
        <KpiCard
          label={t("liKpiShortageRisk")}
          value={fmtBy(isAmt ? kpis.shortageRisk?.amt : kpis.shortageRisk?.qty)}
          /* 결품 = 백오더 최대치. 결품 버킷 수(stockoutDays)를 함께 보여야 "얼마나 오래" 가 읽힌다 */
          sub={`${t("liStockoutDays")} ${kpis.shortageRisk?.stockoutDays ?? 0} · ${t("fillRate")} ${kpis.shortageRisk?.fillRate ?? 0}%`}
          sparkData={payload.spark?.shortageRisk}
          gradientId="sparkLiShortage"
          onClick={drillDefs.shortage.onDrill}
        />
      </DashboardRow>

      {/* Row 2: 거점 보충 네트워크 — 지도(BOD lane) + 거점별 테이블. 선택이 서로 연동된다.
          ★ 지도 링크가 이 화면에서 BOD(origin→dest)를 보여주는 유일한 자리다. */}
      <DashboardRow>
        {mapItems.length > 0 ? (
          <WorldMapPanel
            items={mapItems}
            currencyCd={cc}
            highlighted={selectedLoc}
            onReset={() => setSelectedLoc(null)}
            network={{ nodes: payload.network?.originNodes ?? [], links: payload.network?.links ?? [] }}
            onItemClick={(n) => setSelectedLoc(n.name)}
            /* 지도는 마커·리셋 버튼이 클릭 요소라 제목만 링크. hover 하이라이트는 sx 로 얹는다 */
            title={t("liMapCard")}
            {...titleDrill("map")}
            sx={{ flex: 4, transition: CARD_HOVER_TRANSITION, ...(drillHoverKey === "map" && CARD_HOVER_SX) }}
          />
        ) : (
          <NoGeoPanel />
        )}
        <LocationTable
          title={t("liLocTableCard")}
          rows={locationRows}
          currencyCd={cc}
          unit={unit}
          highlighted={selectedLoc}
          onSelect={(loc) => setSelectedLoc((prev) => (prev === loc ? null : loc))}
          drill={titleDrill("locTable")}
          hovered={drillHoverKey === "locTable"}
        />
      </DashboardRow>

      {/* Row 3: PSI 밸런스(시계열) + 보충 예외 */}
      <DashboardRow>
        <PsiBalanceCard
          title={psiTitle}
          series={psiData}
          colors={psiColors}
          labels={psiLabels}
          chartConfig={chartConfig}
          onToggleSeries={toggleSeries}
          onOpenConfig={() => setConfigOpen(true)}
          buckets={psiBuckets}
          bucket={activeBucket}
          onChangeBucket={setPsiBucket}
          drill={titleDrill("psi")}
          hovered={drillHoverKey === "psi"}
        />
        <ExceptionCard
          title={t("liExceptionCard")}
          rows={payload.exceptionRows}
          currencyCd={cc}
          unit={unit}
          drill={titleDrill("exception")}
          hovered={drillHoverKey === "exception"}
          grouped={!!payload.itemGrouped}
          groupLabel={itemGroupLabel}
        />
      </DashboardRow>

      <ChartConfigModal
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        config={chartConfig}
        onSave={(cfg) => { saveChartConfig(cfg); setConfigOpen(false); }}
        measures={configMeasures}
        chartTypes={DW_CHART_TYPES}
      />
    </DashboardPage>
  );
};

export default LocationInventoryDashboard;
